import React from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns-tz";
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Edit,
    DeleteForever,
    GroupAdd,
    AccountCircle,
    MailOutline,
    AccessTime,
} from '@mui/icons-material';
import useSocket from 'hooks/useSocket';
import { hasPermission } from "state/checkPermission";
import FlexBetween from "components/FlexBetween";


const UserList = ({ rows, onDelete, onEdit, onAuthorize }) => {
    const user = useSelector((state) => state.user);
    const { onlineUsers } = useSocket(user._id); 
    const theme = useTheme();

    
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows?.map((member, index) => {
                const inactive = member.isActive === false;
                const isOnline = onlineUsers?.includes(member._id);
                const loggedAt = member.loggedAt
                    ? format(new Date(member.loggedAt), "HH:mm:ss dd/MM/yyyy")
                    : '';
                
                const canEdit = hasPermission(user?.permissions, "Users", "update", false);
                const canDelete = hasPermission(user?.permissions, "Users", "delete", false);
                const canAuthorize = hasPermission(user?.permissions, "Users", "authorize", false);

                const iconColor = (inactive)
                    ? theme.palette.text.disabled
                    : theme.palette.primary.dark;
                    
                return (
                    <Box
                        key={index}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${inactive
                                ? theme.palette.neutral.light
                                : theme.palette.neutral.deep}`,
                            backgroundColor: inactive
                                ? theme.palette.action.hover
                                : "background.paper",
                            color: inactive
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                boxShadow: inactive ? 1 : 3,
                            },
                            opacity: inactive ? 0.75 : 1,
                        }}
                    >
                        <FlexBetween>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.primary,
                                }}
                            >
                                {member?.lastName || ''} {member?.firstName || ''}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.secondary,
                                }}
                            >
                                {member?.department[0]?.departmentCode || "-"} | {member?.location || "-"}
                            </Typography>
                        </FlexBetween>

                        <Divider sx={{ my: 0.5 }} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AccountCircle
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#0891B2",
                                    }}
                                />
                                <Typography variant="body2">
                                    {member?.role || "-"}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <MailOutline
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#0891B2",
                                    }}
                                />
                                <Typography variant="body2">
                                    {member.email || "-"}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <AccessTime
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#0891B2",
                                    }}
                                />
                                <Typography 
                                    variant="body2" 
                                    sx={{ color: isOnline ? theme.palette.green.deep : "inherit" }}
                                >
                                    {loggedAt || "N/A"}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            sx={{ mt: 1 }}
                        >
                            {canDelete && (
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}
                                        onClick={() => {
                                            canDelete && onDelete(member._id);
                                        }}
                                    >
                                        <DeleteForever
                                            fontSize="small"
                                            sx={{ color: iconColor }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canEdit &&  (
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"                            
                                        onClick={() => {
                                            canEdit && onEdit(member);
                                        }}
                                    >
                                        <Edit
                                            fontSize="small"
                                            sx={{ color: theme.palette.primary.dark }}
                                        />
                                    </IconButton>
                                </Tooltip> 
                            )}  
                            {canAuthorize && (
                                <Tooltip title="Authorize">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}
                                        onClick={() =>
                                            canAuthorize && onAuthorize(member)
                                        }
                                    >
                                        <GroupAdd fontSize="small" sx={{ color: iconColor }} />
                                    </IconButton>
                                </Tooltip>
                            )}                                                                               
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default UserList;
